import React, { useState, useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, Mountain, Castle, Activity } from "lucide-react";
import { featuredAttractions } from "@/data/attractions";
import AttractionModal from "./AttractionModal";
import { StickyScroll } from "@/components/ui/sticky-scroll-reveal";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/data/translations";
import { getTranslatedAttractions } from "@/utils/attractionTranslations";

const QuickAccessTabs = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("all");

  // Get translated attractions based on current language
  const translatedAttractions = useMemo(() => {
    return getTranslatedAttractions(language);
  }, [language]);

  // Filter attractions based on active tab
  const getFilteredAttractions = (tab: string) => {
    if (tab === "all") return translatedAttractions;

    if (tab === "nearby") {
      return translatedAttractions.filter((attraction) => {
        const distance = parseInt(attraction.distance);
        return distance <= 30;
      });
    }

    if (tab === "nature") {
      return translatedAttractions.filter(
        (attraction) => attraction.category === "nature",
      );
    }

    if (tab === "culture") {
      return translatedAttractions.filter(
        (attraction) => attraction.category === "culture",
      );
    }

    if (tab === "activities") {
      return translatedAttractions.filter(
        (attraction) => attraction.category === "activity",
      );
    }

    return translatedAttractions;
  };

  // Transform attractions to sticky scroll content format
  const getStickyScrollContent = (attractions: any[]) => {
    return attractions.map((attraction, index) => ({
      title: attraction.name,
      description: attraction.description,
      image: attraction.image, // Add image URL for mobile background
      content: (
        <div
          className="flex h-full w-full items-center justify-center text-white relative overflow-hidden rounded-lg"
          data-oid="kna17ip"
        >
          <img
            src={attraction.image}
            alt={attraction.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition:
                attraction.id === "schynige-platte"
                  ? "left center"
                  : "center center",
            }}
            data-oid="q6vo73."
          />

          <div
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"
            data-oid="ai_kp7y"
          />

          <div
            className="relative z-10 p-6 text-center h-full flex flex-col justify-end"
            data-oid="u_:-ry5"
          >
            <div className="space-y-3" data-oid="tvhs4sp">
              <div className="flex justify-center" data-oid="mzc_9ty">
                <Badge
                  className="bg-alpine-gold hover:bg-alpine-gold/90 text-white text-sm px-4 py-2 font-semibold shadow-lg"
                  data-oid="lrdpbc2"
                >
                  {attraction.distance}
                </Badge>
              </div>
              <div
                className="flex items-center justify-center gap-2 text-white text-base font-medium"
                data-oid="5f2ywub"
              >
                <Clock className="w-5 h-5" data-oid="tjiry:q" />
                <span data-oid="vnvef5k">{attraction.duration}</span>
              </div>
              <div
                className="flex items-center justify-center gap-2 text-white text-base font-medium"
                data-oid="d2f7n9o"
              >
                <MapPin className="w-5 h-5" data-oid="ndjjoba" />
                <span data-oid="g1ujfqu">{attraction.location}</span>
              </div>
              <div className="pt-2" data-oid="u4rk_-t">
                <AttractionModal attraction={attraction} data-oid="pqaop.p">
                  <Button
                    variant="outline"
                    className="bg-white/20 border-white text-white hover:bg-white hover:text-alpine-gold backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 font-semibold px-6 py-2"
                    data-oid="38pq.jk"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.quickAccess.learnMore",
                    )}
                  </Button>
                </AttractionModal>
              </div>
            </div>
          </div>
        </div>
      ),
    }));
  };

  const tabData = [
    {
      value: "all",
      label: getTranslation(
        language,
        "localAttractions.quickAccess.allAttractions",
      ),
      icon: MapPin,
      description: getTranslation(
        language,
        "localAttractions.quickAccess.allAttractionsDesc",
      ),
    },
    {
      value: "nearby",
      label: getTranslation(
        language,
        "localAttractions.quickAccess.within30min",
      ),
      icon: MapPin,
      description: getTranslation(
        language,
        "localAttractions.quickAccess.within30minDesc",
      ),
    },
    {
      value: "nature",
      label: getTranslation(
        language,
        "localAttractions.quickAccess.natureHiking",
      ),
      icon: Mountain,
      description: getTranslation(
        language,
        "localAttractions.quickAccess.natureHikingDesc",
      ),
    },
    {
      value: "culture",
      label: getTranslation(
        language,
        "localAttractions.quickAccess.cultureHistory",
      ),
      icon: Castle,
      description: getTranslation(
        language,
        "localAttractions.quickAccess.cultureHistoryDesc",
      ),
    },
    {
      value: "activities",
      label: getTranslation(
        language,
        "localAttractions.quickAccess.activities",
      ),
      icon: Activity,
      description: getTranslation(
        language,
        "localAttractions.quickAccess.activitiesDesc",
      ),
    },
  ];

  return (
    <section className="pt-16 pb-8 bg-pebble-beige relative" data-oid="k7ye7.t">
      <div className="container mx-auto px-6" data-oid="ah4r2m6">
        <div
          className="text-center mb-12"
          id="explore-attractions-header"
          data-oid="0.hsbx9"
        >
          <h2
            className="text-3xl md:text-4xl text-gray-900 font-playfair mb-4"
            data-oid="4:hn2kd"
          >
            {getTranslation(language, "localAttractions.quickAccess.title")}
          </h2>
          <p
            className="text-lg text-gray-900 font-lato max-w-2xl mx-auto"
            data-oid="q3rkp0t"
          >
            {getTranslation(
              language,
              "localAttractions.quickAccess.description",
            )}
          </p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
          data-oid="j2vlvjf"
        >
          <TabsList
            className="grid w-full grid-cols-5 h-auto p-1 bg-white/80 backdrop-blur-sm"
            data-oid="8zd36hm"
          >
            {tabData.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex flex-col items-center gap-2 p-4 text-sm font-medium data-[state=active]:bg-alpine-gold data-[state=active]:text-white transition-all duration-300 rounded-full"
                  data-oid="c9xnnl3"
                >
                  <IconComponent className="w-5 h-5" data-oid="ijrpldi" />
                  <span className="hidden sm:block" data-oid="v-:uvn5">
                    {tab.label}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {tabData.map((tab) => {
            const filteredAttractions = getFilteredAttractions(tab.value);
            const stickyScrollContent =
              getStickyScrollContent(filteredAttractions);

            return (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className="mt-0 relative"
                data-oid="dfs.f8c"
              >
                {filteredAttractions.length > 0 ? (
                  <div className="w-full relative" data-oid="dn6-y7.">
                    <StickyScroll
                      content={stickyScrollContent}
                      data-oid="vhsce03"
                    />
                  </div>
                ) : (
                  <div className="text-center py-12" data-oid="as9v__s">
                    <p className="text-gray-900" data-oid="fyfbw4_">
                      {getTranslation(
                        language,
                        "localAttractions.quickAccess.noAttractionsFound",
                      )}
                    </p>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Gradient transition to next section */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, hsl(var(--pebble-beige)) 100%)",
          }}
          data-oid="qvax51k"
        />
      </div>
    </section>
  );
};

export default QuickAccessTabs;
